import styled, { css } from 'styled-components';
import { layoutVariant } from '@sleepunique/types/page-types';

export const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background-color: var(--color-sleepunique-teal);
  background-repeat: no-repeat;
  background-position:  center;
  background-size: cover;
  display: grid;
  grid-template-rows: auto 1fr auto auto;

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
      padding-top: var(--spacing-7);
      background-image: url('/assets/images/sleepunique/grid_white-TEST.svg');
      justify-content: center;
      align-items: flex-start;
  }}

  ${({ theme }) => theme.breakpoints.maxBp('desktop')} {
    &:before {
      position: relative;
      top: 0;
      width: 100%;
      height: 4rem;
      background-size: cover;
      content: '';
      background-image: url('/assets/images/sleepunique/grid-white-tablet-top.svg');
      background-position: bottom center;
      align-self: flex-start;
    }

      &:after {
      position: relative;
      bottom: 0;
      width: 100%;
      height: 12rem;
      background-size: cover;
      content: '';
      background-image: url('/assets/images/sleepunique/grid-white-tablet-bottom.svg');
      background-position: top center;
      background-size: cover;
      align-self: flex-end;
    }
  }

    ${({ theme }) => theme.breakpoints.minBp('tablet')} {

      &:before {
        height: 4.5rem;
        background-image: url('/assets/images/sleepunique/grid-white-tablet-top.svg');
      }

      &:after {
        height: 15rem;
        background-image: url('/assets/images/sleepunique/grid-white-tablet-bottom.svg');
      }
    }
`;

export const Container = styled.div`
  display: grid;
  flex-direction: column;
  width: 100%;
  padding: 0 var(--spacing-4) var(--spacing-5);
  background-color: var(--color-sleepunique-teal);
  position: relative;

  &:before {
    position: absolute;
    right: 0;
    top: var(--spacing-2);
    content: '';
    background-image: url('/assets/images/sleepunique/measurements.svg');
    background-position: center top;
    background-repeat: no-repeat;
    width: 2rem;
    height: 100%;
  }

  ${({ theme }) => theme.breakpoints.minBp('tablet')} {
    padding: 0 var(--spacing-4) var(--spacing-10);
  }

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    width: 83vw;
    height: 70vh;
    padding: 0 var(--spacing-9) var(--spacing-7);
  }
`;

export const Inner = styled.div`
  width: 100%;

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    max-height: 100%;
    overflow-x: auto;
  }
`;

export const Navigation = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-3) 0 var(--spacing-4);
  align-self: flex-start;
  width: 100%;

  ${({ theme }) => theme.breakpoints.minBp('tablet')} {
    flex-direction: row;
    padding: var(--spacing-4) 0 var(--spacing-6);
    gap: 1.5rem;
  }

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    padding: var(--spacing-2) 0 var(--spacing-4);
    gap: 2.875rem;
  }
`;

export const Back = styled.button`
  padding: 0;
  border: none;
  appearance: none;
  background: none;
  color: #fff;
  cursor: pointer;
  font-family: 'Stag Sans';
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.375;
`;

export const ProgressWrapper = styled.label`
  position: relative;
  width: 100%;
`;

export const ProgressIndicator = styled.span`
  position: absolute;
  top: 50%;
  right: 0.2em;
  display: block;
  padding: 0.2em;
  background-color: var(--color-sleepunique-teal);
  color: var(--color-sleepunique-light-red);
  font-family: 'Stag Sans';
  font-size: 0.75rem;
  font-weight: 300;
  line-height: 1;
  transform: translateY(-50%);
`;

export const Progress = styled.div`
  width: 100%;
  height: 1.25rem;
  border: 1px solid var(--color-sleepunique-light-red);
`;

export const ProgressInner = styled.div`
  height: 100%;
  background-color: var(--color-sleepunique-light-red);
  transition: width 100ms ease-in-out;
`;

export const Content = styled.div``;

type ImageProps = {
  $orientation: layoutVariant;
};

const commonImageStyles = css`
  display: none;
  position: absolute;
  width: auto;
  z-index: 10;
  pointer-events: none;

  ${({ theme }) => theme.breakpoints.minBp('tablet')} {
    display: block;
  }
`;

export const Chair = styled.img<ImageProps>`
  ${commonImageStyles}
  height: 10rem;

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    height: 12rem;
  }

  ${({ $orientation = 'a' }) => {
    switch ($orientation) {
      case 'c':
        return css`
          bottom: -9rem;
          right: calc(100% - 19rem);

          ${({ theme }) => theme.breakpoints.minBp('tablet')} {
            @media (max-width: 834px) {
              display: none;
            }
            left: calc(100% - 11rem);
          }

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -6rem;
            right: calc(100% - 18rem);
          }
        `;
      case 'c.reversed':
        return css`
          bottom: -9rem;
          right: calc(100% - 12rem);
          transform: rotateY(180deg);
          z-index: 2;

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -6rem;
            right: calc(100% - 18rem);
          }
        `;
      case 'b':
        return css`
          bottom: -9rem;
          left: calc(100% - 11rem);

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -7rem;
          }
        `;
      case 'b.reversed':
        return css`
          bottom: -9rem;
          right: calc(100% - 10rem);
          transform: rotateY(180deg);

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -7rem;
          }
        `;
      case 'a':
      default:
        return css`
          bottom: -9rem;
          left: calc(100% - 10rem);
          ${({ theme }) => theme.breakpoints.minBp('tablet')} {
            @media (max-width: 834px) {
              display: none;
            }
          }

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -7rem;
            left: calc(100% - 11rem);
          }
        `;
    }
  }}
`;

export const Plant = styled.img<ImageProps>`
  ${commonImageStyles}
  height: 10rem;

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    height: 13rem;
  }

  ${({ $orientation = 'a' }) => {
    switch ($orientation) {
      case 'c':
        return css`
          bottom: -9rem;
          right: calc(100% - 10rem);

          ${({ theme }) => theme.breakpoints.minBp('tablet')} {
            @media (max-width: 834px) {
              display: none;
            }
            left: 5rem;
          }

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -6rem;
            right: calc(100% - 6rem);
          }
        `;
      case 'c.reversed':
        return css`
          bottom: -9rem;
          right: calc(100% - 18rem);
          z-index: 1;

          ${({ theme }) => theme.breakpoints.minBp('tablet')} {
            @media (max-width: 834px) {
              right: calc(100% - 22rem);
            }

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -6rem;
            right: calc(100% - 18rem);
          }
        `;
      case 'b':
        return css`
          bottom: -9rem;
          left: 0;

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -7rem;
          }
        `;
      case 'b.reversed':
        return css`
          bottom: -9rem;
          left: calc(100% - 7rem);

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -7rem;
          }
        `;
      case 'a':
      default:
        return css`
          bottom: -9rem;
          left: calc(100% - 16rem);

          ${({ theme }) => theme.breakpoints.minBp('tablet')} {
            @media (max-width: 834px) {
              display: none;
            }
            left: 5rem;
          }

          ${({ theme }) => theme.breakpoints.minBp('desktop')} {
            bottom: -6rem;
            left: calc(100% - 17rem);
          }
        `;
    }
  }}
`;

export const ResultsWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  overflow: hidden;
  align-items: flex-start;
  padding-top: var(--spacing-8);
  width: 100%;

  background: var(--color-sleepunique-lighter-blue);
  background-repeat: no-repeat;
  background-position: top center;
  background-image: url('/assets/images/sleepunique/perspective-grid-light.svg');
  background-size: 100%;
  background-image: linear-gradient(
      to bottom,
      rgba(125, 185, 232, 0) 0%,
      rgba(227, 242, 245, 1) 70%,
      rgba(227, 242, 245, 1) 100%
    ),
    url('/assets/images/sleepunique/perspective-grid-light.svg');

  ${({ theme }) => theme.breakpoints.minBp('desktop')} {
    padding-top: var(--spacing-14);
  }
`;

export const ResultsContainer = styled.div`
  display: flex;
  flex-direction: columns;
  align-content: center;
  text-align: center;
  width: 100%;
`;
